import { Spinner } from '@chakra-ui/react'
import { API_KEY } from '@core/utils/constants/constants'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'
import { useTranslation } from 'react-i18next'
import type {
    DropdownIndicatorProps,
    GroupBase,
    PlaceholderProps,
    StylesConfig,
} from 'react-select'

interface Props {
    placeholder?: string
    value?: Values | null
    onChange?: (value: Values) => void
}

export interface Values {
    label?: string
    country?: string
    city?: string
    postalCode?: number | string | null
    address1?: string
}

export const GoogleAutocompleteStandalone = ({ placeholder, value, onChange }: Props) => {
    const { t } = useTranslation()

    const getFullAddress = async (placeId: string, label: string) => {
        if (placeId.length > 0) {
            const [address] = await geocodeByPlaceId(placeId)
            const { long_name: postalCode = '' } =
                address.address_components.find((c) => c.types.includes('postal_code')) || {}
            const { long_name: country = '' } =
                address.address_components.find((c) => c.types.includes('country')) || {}
            const { long_name: city = '' } =
                address.address_components.find(
                    (c) =>
                        c.types.includes('locality') ||
                        c.types.includes('administrative_area_level_1') ||
                        c.types.includes('postal_town')
                ) || {}
            const { long_name: addressStr = '' } = address.address_components.find((c) => c.types.includes('route')) || {}
            const { long_name: street_number = '' } = address.address_components.find((c) => c.types.includes('street_number')) || {}
            const fullAddress = {
                postalCode,
                country,
                city,
                address1: (addressStr + ' ' + street_number).trim(),
                label,
            }

            onChange?.(fullAddress)
        }
    }

    const styles: StylesConfig = {
        control: (base, state) => ({
            ...base,
            height: '44px',
            borderColor: '#E0E0E0',
            outline: 'none',
            boxShadow: 'none',
            paddingLeft: '8px',
            paddingRight: '8px',
            color: '#1A1A1A',
            fontSize: '14px',
            '&:hover': {
                borderColor: state.isFocused ? '#2F80ED' : '#E0E0E0',
            },
        }),
        placeholder: (provided: PlaceholderProps) => ({
            ...provided,
            fontSize: '14px',
        }),
    }

    return (
        <GooglePlacesAutocomplete
            apiKey={API_KEY}
            selectProps={{
                classNamePrefix: 'an-simple-select',
                noOptionsMessage: () => `${t('2935')}...`,
                loadingMessage: () => `${t('2935')}...`,
                placeholder: placeholder || `${t('1979')}`,
                value: value ? { label: value.label, value: value.label } : null,
                onChange: ({ value, label }: any) => {
                    if (value !== undefined) {
                        getFullAddress(value.place_id, label)
                    }
                },
                components: {
                    DropdownIndicator: () => null,
                    LoadingIndicator: () => <Spinner color="state.primary" mr={'1'} size={'xs'} />,
                    IndicatorSeparator: () => null,
                },
                styles,
            }}
        />
    )
}